import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { BarChart3 } from 'lucide-react';

/**
 * Admin aggregate analytics for the Services Marketplace (W4, mirrors the other admin
 * services sections). Renders GET /api/admin/analytics/services — AGGREGATES ONLY, the
 * endpoint never returns client identities/PII. Read-only; no actions.
 */
const AdminServicesAnalyticsSection = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: session } = await supabase.auth.getSession();
        if (!session?.session?.access_token) return;
        const res = await api.get('/api/admin/analytics/services', {
          headers: { Authorization: `Bearer ${session.session.access_token}` },
        });
        if (!cancelled) setData(res.data || null);
      } catch (error) {
        // 404 = services flag off; anything else logs. Either way the card shows the empty state.
        console.error('[AdminServicesAnalyticsSection] Failed to fetch analytics:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const pct = (v) => (v === null || v === undefined ? '—' : `${Math.round(v * 1000) / 10}%`);
  const num = (v) => (v === null || v === undefined ? '—' : v);

  const stats = data ? [
    { label: t('services.adminAnalyticsListings'), value: `${num(data.listings?.active)} / ${num(data.listings?.total)}`, hint: t('services.adminAnalyticsActiveOfTotal') },
    { label: t('services.adminAnalyticsContacts'), value: num(data.contacts?.total_clicks), hint: t('services.adminAnalyticsPendingPrompts', { count: data.contacts?.pending_prompts ?? 0 }) },
    { label: t('services.adminAnalyticsHireRate'), value: pct(data.contacts?.hire_confirmed_rate), hint: t('services.adminAnalyticsHireRateHint') },
    { label: t('services.adminAnalyticsReviews'), value: `${num(data.reviews?.released)} / ${num(data.reviews?.total_pro_reviews)}`, hint: t('services.adminAnalyticsReleasedOfTotal') },
    { label: t('services.adminAnalyticsRequests'), value: `${num(data.requests?.open)} / ${num(data.requests?.total)}`, hint: t('services.adminAnalyticsOpenOfTotal') },
    { label: t('services.adminAnalyticsPickedRate'), value: pct(data.requests?.picked_rate), hint: t('services.adminAnalyticsAvgResponses', { count: data.requests?.avg_responses ?? '—' }) },
  ] : [];

  const topCategories = data ? Object.entries(data.listings?.by_category || {}).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];
  const topMunicipalities = data ? Object.entries(data.listings?.by_municipality || {}).sort((a, b) => b[1] - a[1]).slice(0, 8) : [];
  const topTerms = data?.search_misses?.top_terms || [];
  const monthly = data?.monthly || [];

  return (
    <Card data-testid="services-analytics-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 size={18} className="text-primary" aria-hidden="true" />
          {t('services.adminAnalyticsTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t('common.loading', 'Loading...')}</p>
        ) : !data ? (
          <p className="text-sm text-muted-foreground text-center py-8">{t('services.adminAnalyticsEmpty')}</p>
        ) : (
          <div className="space-y-6">
            {/* Headline stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {stats.map((s) => (
                <div key={s.label} className="rounded-lg border bg-muted/30 p-3">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <p className="text-xl font-bold">{s.value}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{s.hint}</p>
                </div>
              ))}
            </div>

            {/* Breakdown lists */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { title: t('services.adminAnalyticsByCategory'), rows: topCategories },
                { title: t('services.adminAnalyticsByMunicipality'), rows: topMunicipalities },
                { title: t('services.adminAnalyticsTopTerms'), rows: topTerms.map((x) => [x.term, x.count]) },
              ].map((block) => (
                <div key={block.title}>
                  <h4 className="text-sm font-semibold mb-2">{block.title}</h4>
                  {block.rows.length === 0 ? (
                    <p className="text-xs text-muted-foreground">{t('services.adminAnalyticsNoData')}</p>
                  ) : (
                    <ul className="space-y-1">
                      {block.rows.map(([name, count]) => (
                        <li key={name} className="flex justify-between text-sm">
                          <span className="truncate mr-2" title={name}>{name}</span>
                          <span className="font-medium tabular-nums">{count}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* 6-month trend */}
            <div>
              <h4 className="text-sm font-semibold mb-2">{t('services.adminAnalyticsMonthly')}</h4>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[360px] text-sm">
                  <thead className="border-b bg-muted/30">
                    <tr>
                      <th className="py-2 px-3 text-left font-semibold">{t('services.adminAnalyticsMonth')}</th>
                      <th className="py-2 px-3 text-right font-semibold">{t('services.adminAnalyticsMonthlyClicks')}</th>
                      <th className="py-2 px-3 text-right font-semibold">{t('services.adminAnalyticsMonthlyNewListings')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {monthly.map((m) => (
                      <tr key={m.month}>
                        <td className="py-1.5 px-3">{m.month}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{m.clicks}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{m.new_listings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminServicesAnalyticsSection;
